import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Form, FormField, FormItem, FormLabel, FormMessage, Textarea } from '@kalos/ui';
import { type ReactNode, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { type z } from 'zod';

import { DialogOrSheet } from '../../../components/DialogOrSheet';
import { type SaveHandler } from '../../../tools/helpers';

type Step = 'edit' | 'confirmDelete' | 'confirmDiscard';

/**
 * A customer or property notification on phones and tablets: a bottom sheet that opens ready to
 * type in, rather than desktop's read-then-Edit dialog. Deleting and discarding unsaved text are
 * asked inside the same sheet, never in a dialog on top of it.
 */
export const NotificationSheet = <TSchema extends z.ZodType<{ notification: string }>>({
  open,
  onOpenChange,
  trigger,
  onSave,
  disabled,
  loading,
  defaultValue,
  onDelete,
  title,
  bottomContent,
  schema,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  trigger?: ReactNode;
  onSave: SaveHandler<{ notification: string }>;
  disabled?: boolean;
  loading?: boolean;
  defaultValue: string;
  onDelete?: () => void | Promise<void>;
  title?: string;
  bottomContent?: ReactNode;
  schema: TSchema;
}) => {
  const [step, setStep] = useState<Step>('edit');
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const form = useForm<{ notification: string }>({
    resolver: zodResolver(schema),
    defaultValues: { notification: defaultValue },
  });

  // Each time it opens, it starts from the saved text.
  useEffect(() => {
    if (open) {
      form.reset({ notification: defaultValue });
      setStep('edit');
    }
  }, [open, defaultValue, form]);

  const isBusy = isSaving || isDeleting || !!loading;

  const requestClose = (nextOpen: boolean) => {
    if (nextOpen) {
      onOpenChange(true);
      return;
    }
    if (step === 'edit' && form.formState.isDirty) {
      setStep('confirmDiscard');
      return;
    }
    onOpenChange(false);
  };

  const submit = form.handleSubmit(async (data) => {
    setIsSaving(true);
    try {
      await onSave({ data, dirtyFields: form.formState.dirtyFields });
    } finally {
      setIsSaving(false);
    }
  });

  const remove = async () => {
    setIsDeleting(true);
    try {
      await onDelete?.();
    } finally {
      setIsDeleting(false);
    }
  };

  const heading =
    step === 'confirmDelete'
      ? 'Delete this notification?'
      : step === 'confirmDiscard'
        ? 'Discard your changes?'
        : title ?? 'Notification';

  return (
    <DialogOrSheet
      open={open}
      onOpenChange={requestClose}
      title={heading}
      trigger={trigger}
      isBusy={isBusy}
    >
      {step === 'confirmDelete' ? (
        <div className="flex flex-col gap-4">
          <p className="text-muted-foreground text-sm">
            It&apos;s removed for everyone who opens this record, and can&apos;t be undone.
          </p>
          <div className="flex flex-col gap-2">
            <Button
              variant="destructive"
              disabled={isBusy}
              isLoading={isDeleting}
              onClick={() => void remove()}
            >
              Delete notification
            </Button>
            <Button variant="ghost" disabled={isBusy} onClick={() => setStep('edit')}>
              Keep notification
            </Button>
          </div>
        </div>
      ) : step === 'confirmDiscard' ? (
        <div className="flex flex-col gap-4">
          <p className="text-muted-foreground text-sm">The text you typed won&apos;t be saved.</p>
          <div className="flex flex-col gap-2">
            <Button variant="destructive" onClick={() => onOpenChange(false)}>
              Discard changes
            </Button>
            <Button variant="ghost" onClick={() => setStep('edit')}>
              Keep editing
            </Button>
          </div>
        </div>
      ) : (
        <Form {...form}>
          <form onSubmit={(event) => void submit(event)} className="flex flex-col gap-4">
            <FormField
              control={form.control}
              name="notification"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Notification</FormLabel>
                  <Textarea
                    {...field}
                    autoGrow={{ minRows: 4, maxRows: 12 }}
                    disabled={isBusy || disabled}
                  />
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button type="submit" disabled={isBusy || disabled} isLoading={isSaving}>
              {isSaving ? 'Saving…' : 'Save notification'}
            </Button>
            {onDelete && (
              <Button
                type="button"
                variant="ghost"
                className="text-destructive mt-2"
                disabled={isBusy}
                onClick={() => setStep('confirmDelete')}
              >
                Delete notification
              </Button>
            )}
          </form>
        </Form>
      )}
      {step === 'edit' && bottomContent}
    </DialogOrSheet>
  );
};
